'use client';

import { Fragment } from 'react';

import type { GetResponseType } from '@/api/types/api';
import type { ApiResponseDefinition } from '@/api/types/api-definition';
import Image from '@/components/Image';
import { useCustomerService } from '@/hooks/useCustomerService';
import { useTranslations } from '@/i18n/use-translations';
import { IS_US } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { setSession } from '@/utils/tools';
import {
  FacebookOutlined,
  HelpEmailOutlined,
  HelpOnlineOutlined,
  HelpPhoneOutlined,
  InstagramOutlined,
  PinterestOutlined,
  TiktokOutlined,
  TwitterOutlined,
  YoutubeOutlined,
} from '@zeelool/icon';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

import { shouldShowUSFooterSubscribe } from '../../subscribeVisibility';
import CountrySiteList2026 from './CountrySiteList';
import Subscribe2026 from './Subscribe';
import SubscribeCoupon2026 from './SubscribeCoupon';
import { getFooterLegalLinks, LEGAL_LINKS_TEMPLATE_ID } from './legalLinks';
import { getFooterPaymentModules, PAYMENT_TEMPLATE_ID } from './paymentModules';
import { getFooterCopyrightModules, COPYRIGHT_TEMPLATE_ID } from './copyrightModules';

interface FooterNavLink {
  href?: string;
  title: string;
}

interface FooterNavContent {
  describe?: string;
  layout_title?: string;
  list?: Array<FooterNavLink | null>;
}

interface Footer2026Props {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  SDSocialMediaClick: (btn: string) => void;
  SDBottomNavTabClick: (
    first_tab_name: string,
    second_tab_name?: string,
    first_tab_index?: number,
    second_tab_index?: number,
  ) => void;
  isShowSubscribe: boolean;
  deviceType: EPlatform;
}

export default function Footer2026({
  navList,
  SDSocialMediaClick,
  SDBottomNavTabClick,
  isShowSubscribe,
  deviceType,
}: Footer2026Props) {
  const bottomSubscribeBoxTxt =
    (navList[0]?.content as FooterNavContent | undefined)?.describe || '';
  const pathname = usePathname();
  const t = useTranslations('layout.footer');
  const router = useRouter();
  const isShowCouponSubscribe = !IS_US && isShowSubscribe;
  const isShowSubscribeUS = shouldShowUSFooterSubscribe(pathname);

  const { openCustomerService } = useCustomerService({
    inAppH5: false,
  });

  const legalLinks = getFooterLegalLinks(navList);
  const paymentModules = getFooterPaymentModules(navList, env.NEXT_PUBLIC_S3_URL);
  const copyrightModules = getFooterCopyrightModules(navList);

  return (
    <footer
      className={`bg-black text-white ${pathname.includes('/goods-detail/') ? 'pb-[80px]' : ''}`}
    >
      {isShowSubscribeUS ? <Subscribe2026 /> : null}
      {isShowCouponSubscribe ? (
        <SubscribeCoupon2026 bottomSubscribeBoxTxt={bottomSubscribeBoxTxt} />
      ) : null}

      <div className="flex w-full items-start justify-between border-y border-[#6c6c6c] px-[72px] py-[24px]">
        <div className="flex shrink-0 flex-col items-start gap-[32px]">
          <b className="font-bb text-[48px] leading-[58px] text-white">{t('needHelp')}</b>

          <dl className="flex w-full flex-col gap-[20px]">
            <div className="flex min-h-[32px] items-center gap-[8px]">
              <dt className="flex size-[32px] shrink-0 items-center justify-center">
                <HelpOnlineOutlined className="text-[20px] text-white" />
              </dt>
              <dd className="text-[14px] leading-[17px]">
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <button
                    type="button"
                    className="cursor-pointer border-0 bg-transparent p-0 font-bb text-white"
                    aria-label={t('liveChat')}
                    onClick={openCustomerService}
                  >
                    {t('liveChat')}
                  </button>
                ) : (
                  <Link
                    prefetch={false}
                    className="font-bb text-white"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick('Need Help?', 'Contact Us', 0, 0);
                      router.push('/contact-zeelool');
                    }}
                  >
                    {t('contactUs')}
                  </Link>
                )}
              </dd>
            </div>

            <div className="flex min-h-[32px] items-center gap-[8px]">
              <dt className="flex size-[32px] shrink-0 items-center justify-center">
                <HelpEmailOutlined className="text-[24px] text-white" />
              </dt>
              <dd className="flex flex-col gap-[4px] text-[14px] leading-[17px]">
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <Link
                    prefetch={false}
                    className="font-bb text-white"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick('Need Help?', 'Contact Us', 0, 0);
                      router.push('/contact-zeelool');
                    }}
                  >
                    {t('ContactUsViaForm')}
                  </Link>
                ) : (
                  <span className="font-bb">{t('businessCooperation')}</span>
                )}
                <Link
                  prefetch={false}
                  className="font-br text-white"
                  href={`mailto:${env.NEXT_PUBLIC_MARKET_EMAIL}`}
                >
                  {env.NEXT_PUBLIC_MARKET_EMAIL}
                </Link>
              </dd>
            </div>

            {env.NEXT_PUBLIC_FOOTER_SHOW_TEL === 'true' ? (
              <div className="flex min-h-[32px] items-center gap-[8px]">
                <dt className="flex size-[32px] shrink-0 items-center justify-center">
                  <HelpPhoneOutlined className="text-[20px] text-white" />
                </dt>
                <dd className="flex flex-col gap-[4px] font-br text-white">
                  <span className="text-[14px] leading-[17px]">{t('callInfo')}</span>
                  <span className="text-[12px] leading-[14px]">
                    {env.NEXT_PUBLIC_CUSTOMER_SERVICE_TIME}
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>

          <Link
            prefetch={false}
            href="/track-order"
            aria-label={t('trackOrder')}
            className="flex h-[40px] w-[152px] items-center justify-center rounded-[20px] bg-primary-purple font-bb text-[14px] leading-[17px] text-white"
            onClick={(event) => {
              event.preventDefault();
              SDBottomNavTabClick('Need Help?', 'Track Order', 0, 0);
              router.push('/track-order');
            }}
          >
            {t('trackOrder')}
          </Link>

          <div className="flex flex-col gap-[12px]">
            <p className="font-br text-[14px] leading-[17px]">{t('followUs')}</p>
            <div className="flex items-center gap-[24px]">
              {env.NEXT_PUBLIC_FB ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_FB}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('fb')}
                >
                  <FacebookOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_YTB ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_YTB}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('youtube')}
                >
                  <YoutubeOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_INS ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_INS}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('ins')}
                >
                  <InstagramOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_X ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_X}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('twitter')}
                >
                  <TwitterOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_PIN ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_PIN}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('pinterest')}
                >
                  <PinterestOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
              {env.NEXT_PUBLIC_TT ? (
                <Link
                  prefetch={false}
                  href={env.NEXT_PUBLIC_TT}
                  aria-label={t('social.joinGroup')}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                  onClick={() => SDSocialMediaClick('tiktok')}
                >
                  <TiktokOutlined className="text-[24px] text-white" />
                </Link>
              ) : null}
            </div>
            {env.NEXT_PUBLIC_WEB_SHOW_FB_GROUP === 'true' ? (
              <Link
                prefetch={false}
                href={env.NEXT_PUBLIC_FB_GROUP}
                aria-label={t('social.joinGroup')}
                target="_blank"
                rel="noopener nofollow noreferrer"
              >
                <Image
                  src={env.NEXT_PUBLIC_FB_GROUP_IMG}
                  alt={t('social.joinGroup')}
                  width={204}
                  height={48}
                />
              </Link>
            ) : null}
          </div>

          <CountrySiteList2026
            onCountryClick={(shortName) => SDBottomNavTabClick(`Region_${shortName}`)}
            deviceType={deviceType}
          />
        </div>

        <nav
          className="flex min-w-0 flex-1 flex-wrap items-start justify-end gap-[40px]"
          aria-label="Footer"
        >
          {navList.map((item, index) => {
            // 协议、支付和版权模板在底部独立展示，保留其他导航的原始埋点索引。
            if (
              item.template_id === LEGAL_LINKS_TEMPLATE_ID ||
              item.template_id === PAYMENT_TEMPLATE_ID ||
              item.template_id === COPYRIGHT_TEMPLATE_ID
            )
              return null;
            const content = item.content as FooterNavContent;

            // 政策栏等模板也有 list，但不是 href 导航，不能交给 Next Link。
            if (
              (item.template_id != null && item.template_id !== 1) ||
              !Array.isArray(content?.list)
            ) {
              return null;
            }

            return (
              <div key={item.id} className="w-[220px] shrink-0">
                <p className="mb-[16px] font-bb text-[16px] leading-[19px] text-white">
                  {content.layout_title}
                </p>
                <div className="flex flex-col items-start gap-[12px]">
                  {content.list.map((navItem, navItemIndex) => {
                    if (typeof navItem?.href !== 'string' || !navItem.href.trim()) return null;
                    const href = navItem.href.trim();

                    return (
                      <Link
                        prefetch={false}
                        href={href}
                        className="font-br text-[14px] leading-[17px] text-[#999999] hover:text-white"
                        key={`${navItem.title}-${navItemIndex}`}
                        onClick={(event) => {
                          event.preventDefault();
                          SDBottomNavTabClick(
                            content.layout_title || '',
                            navItem.title,
                            index,
                            navItemIndex,
                          );
                          if (href.includes('goods-list')) {
                            setSession('zl_goodsListEnterPageScene', '全局导航');
                          }
                          if (href === env.NEXT_PUBLIC_COOKIE_TARGET) {
                            window.OneTrust.ToggleInfoDisplay();
                          } else if (
                            (href.includes('zeelool.com') || href.includes('xmslol.cn')) &&
                            !href.includes(env.NEXT_PUBLIC_HELP_CENTER_PATH)
                          ) {
                            const targetUrl = new URL(href);
                            const url = targetUrl.href.replace(targetUrl.origin, '');
                            router.push(url);
                          } else {
                            window.location.href = href;
                          }
                        }}
                      >
                        {navItem.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="flex w-full flex-col items-center pt-[24px]">
        {legalLinks.length > 0 ? (
          <ul className="flex w-full flex-wrap items-center justify-center gap-x-[32px] gap-y-[16px] px-[160px] pb-[12px] pt-[24px] font-br text-[14px] uppercase leading-[17px] text-white">
            {legalLinks.map((legalLink, index) => (
              <Fragment key={legalLink.key}>
                {index > 0 ? <li aria-hidden="true">|</li> : null}
                <li className="whitespace-nowrap text-center">
                  <Link
                    prefetch={false}
                    href={legalLink.href}
                    aria-label={legalLink.label}
                    className="text-white"
                    onClick={(event) => {
                      if (
                        legalLink.href === env.NEXT_PUBLIC_COOKIE_TARGET &&
                        env.NEXT_PUBLIC_ENABLE_ONETRUST === 'true' &&
                        typeof window.OneTrust?.ToggleInfoDisplay === 'function'
                      ) {
                        event.preventDefault();
                        window.OneTrust.ToggleInfoDisplay();
                      }
                    }}
                  >
                    {legalLink.label}
                  </Link>
                </li>
              </Fragment>
            ))}
          </ul>
        ) : null}

        {paymentModules.map((payment) => (
          <div
            key={payment.key}
            className="flex w-full items-center justify-center px-[160px] py-[12px]"
          >
            <div className="flex min-w-0 flex-wrap items-center justify-center gap-[12px]">
              {payment.title ? (
                <p className="whitespace-nowrap font-bb text-[14px] leading-[17px] text-white">
                  {payment.title}
                </p>
              ) : null}
              <div
                className="flex flex-wrap items-center justify-center gap-[8px]"
                aria-hidden="true"
              >
                {payment.images.map((src, index) => (
                  <Image
                    key={`${src}:${index}`}
                    src={src}
                    alt=""
                    width={40}
                    height={24}
                    unoptimized
                    className="h-[24px] w-[40px] shrink-0 rounded-[2px] object-contain"
                  />
                ))}
              </div>
            </div>
          </div>
        ))}

        {copyrightModules.map((copyright) => (
          <p
            key={copyright.key}
            className="w-full px-[160px] pb-[40px] pt-[12px] text-center font-br text-[14px] leading-[17px] text-white"
          >
            {copyright.text}
          </p>
        ))}
      </div>
    </footer>
  );
}
